import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { Feature, Geometry } from 'geojson';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { MapBoxStyle } from './map-box-style.enum';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { MapBoxMapLockControl } from './custom-map-controls/map-box-map-lock-control';
import { MapBoxMapRefocusControl } from './custom-map-controls/map-box-map-refocus-control';

export enum MapBoxMode {
  GpsAreaMode,
  LineMode,
  PositionMode
}

@Component({
  selector: 'app-map-box-map',
  standalone: true,
  imports: [CommonModule, ButtonModule, NgxMapboxGLModule],
  templateUrl: './map-box-map.component.html',
  styleUrl: './map-box-map.component.scss'
})
export class MapBoxMapComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input()
  public mapMode: MapBoxMode;

  @Input()
  public isReadOnly: boolean = false;

  @Input()
  public height = '300px';

  @Input()
  public style: MapBoxStyle = MapBoxStyle.SatelliteStreets;

  @Input()
  public mapId: string = 'map';

  //---------------------------------------------
  //GPS Area
  @Input()
  public set gpsArea(value: string) {
    if (value === this._gpsArea) {
      return;
    }
    this._gpsArea = value;
    //Workaround to make sure set gpsArea is always called after map is loaded the first time
    //map status can change during locating to new zone area
    if (this.map && (this.map.loaded() || this.mapLoaded)) {
      if (this._gpsArea) {
        this.setContentAreaForMap();
      }
      else {
        this.resetMap();
      }
    }
  }

  public get gpsArea(): string {
    return this._gpsArea;
  }

  @Output()
  public gpsAreaChange = new EventEmitter<string>();
  //--------------------------------------------
  //Line
  @Input()
  public set line(value: string) {
    if (value === this._line) {
      return;
    }
    this._line = value;
    //Workaround to make sure set line is always called after map is loaded the first time
    //map status can change during locating to new line area
    if (this.map && (this.map.loaded() || this.mapLoaded)) {
      if (this._line) {
        this.setContentAreaForMap();
      }
      else {
        this.resetMap();
      }
    }
  }

  public get line(): string {
    return this._line;
  }

  @Output()
  public lineChange = new EventEmitter<string>();
  //---------------------------------------------
  //Position
  @Input()
  public position: string;

  @Output()
  public positionChange = new EventEmitter<string>();
  //--------------------------------------------

  public isLocked = true;

  private map: mapboxgl.Map;
  private mapLoaded = false;
  private _gpsArea: string;
  private _line: string;
  private marker: mapboxgl.Marker;

  //Coordinates for Switzerland
  private lat: number = 46.80121;
  private lng: number = 8.226692;

  private onMapLoadReference: any;
  private handleDrawCreateReference: any;
  private handleDrawUpdateReference: any;
  private handleDrawDeleteReference: any;
  private newPositionSelectedReference: any;
  private geoCoderNewPositionSelectedReference: any;

  private drawControl: MapboxDraw;
  private fullScreenControl: mapboxgl.FullscreenControl;
  private lockControl: MapBoxMapLockControl;
  private refocusControl: MapBoxMapRefocusControl;
  private geoCoderControl: MapboxGeocoder;

  constructor(private el: ElementRef,
    private renderer: Renderer2) {
  }

  ngOnInit(): void {
    //Define lock state
    this.isLocked = (this.mapMode == MapBoxMode.GpsAreaMode && !!this._gpsArea) || (this.mapMode == MapBoxMode.LineMode && !!this._line) || (this.mapMode == MapBoxMode.PositionMode && !!this.position);

    this.fullScreenControl = new mapboxgl.FullscreenControl()
    this.lockControl = new MapBoxMapLockControl(this.isLocked, this.toggleLockMap.bind(this));
    this.refocusControl = new MapBoxMapRefocusControl(this.refocusMap.bind(this));
    this.geoCoderControl = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      mapboxgl: mapboxgl
    });

    // Bind the functions to the class to be able to remove the event listeners later
    // Need to have the same reference content when adding and removing the event listeners
    this.onMapLoadReference = this.handleMapLoad.bind(this);
    this.handleDrawCreateReference = this.handleDrawCreate.bind(this);
    this.handleDrawUpdateReference = this.handleDrawUpdate.bind(this);
    this.handleDrawDeleteReference = this.handleDrawDelete.bind(this);
    this.newPositionSelectedReference = this.newPositionSelected.bind(this);
    this.geoCoderNewPositionSelectedReference = this.geoCoderNewPositionSelected.bind(this);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // All events for map:
      // https://docs.mapbox.com/mapbox-gl-js/api/map/#map-events
      this.initializeMap();
      this.setHeight();
    }, 100);
  }

  ngOnDestroy(): void {
    this.map.off('load', this.onMapLoadReference);
    this.map.off('draw.delete', this.handleDrawDeleteReference);
    this.map.off('draw.update', this.handleDrawUpdateReference);
    this.map.off('draw.create', this.handleDrawCreateReference);
    this.map.off('click', this.newPositionSelectedReference);
    if (this.map.hasControl(this.geoCoderControl)) {
      this.geoCoderControl?.off('result', this.geoCoderNewPositionSelectedReference);
    }
  }

  private setContentAreaForMap(): void {
    this.isLocked = true;
    this.addFeatureToMap();
    if (this.mapMode == MapBoxMode.GpsAreaMode) {
      this.toggleAddPolygonDrawControl(false);
    } else if (this.mapMode == MapBoxMode.LineMode) {
      this.toggleAddLineDrawControl(false);
    }
    this.centerMapToFeature();
    this.toggleDeleteDrawControl(false);
    this.handleLockStatusChange();
  }

  private resetMap() {
    this.isLocked = false;
    if (!this.map) {
      return;
    }
    this.drawControl?.deleteAll();
    this.toggleAddPolygonDrawControl(true);
    this.map.setCenter([this.lng, this.lat]);
    this.map.setZoom(7);
    this.toggleSearchControl();
    this.handleLockStatusChange();
  }

  private initializeMap() {
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: this.mapId,
      style: this.style,
      zoom: 7,
      center: [this.lng, this.lat]
    });
    this.map.on('load', this.onMapLoadReference);

    //Add controls
    this.map.addControl(this.fullScreenControl, 'top-right');
    this.map.addControl(this.refocusControl, 'top-right');
    if (!this.isReadOnly) {
      this.map.addControl(this.lockControl, 'top-right');
    }
    this.toggleSearchControl();

    if (this.mapMode == MapBoxMode.PositionMode) {
      this.geoCoderControl.on('result', this.geoCoderNewPositionSelectedReference);
    }
  }

  private toggleLockMap(): void {
    this.isLocked = !this.isLocked;
    this.handleLockStatusChange();
  }

  private handleLockStatusChange() {
    this.toggleSearchControl();
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.isLocked ? this.drawControl?.changeMode('static') : this.drawControl?.changeMode('simple_select');
      this.toggleDeleteDrawControl(!this.isLocked);
      if (this.mapMode == MapBoxMode.GpsAreaMode) {
        this.toggleAddPolygonDrawControl(!(this.isLocked || this._gpsArea));
      } else if (this.mapMode == MapBoxMode.LineMode) {
        this.toggleAddLineDrawControl((!this.isLocked || !this._line));
      }

    } else if (this.mapMode == MapBoxMode.PositionMode) {
      this.togglePositionListener();
    }
  }

  private handleMapLoad(): void {
    //Add (zone / polygon) or line functionality if activated
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.addMapboxDrawAndHandleChanges();
    }

    //Add position functionality if activated
    if (this.mapMode == MapBoxMode.PositionMode) {
      this.addPositionMarkerAndHandleChanges();
    }
    this.mapLoaded = true;

    this.handleReadyOnlyMode();
  }

  private addMapboxDrawAndHandleChanges(): void {
    //Declare custom mode "static" for mapbox draw
    //https://github.com/mapbox/mapbox-gl-draw-static-mode
    var modes = MapboxDraw.modes;
    modes.static = StaticMode;

    //Doc for mapbox draw
    //https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md
    this.drawControl = new MapboxDraw({
      modes: modes,
      displayControlsDefault: false,
      controls: {
        polygon: this.mapMode == MapBoxMode.GpsAreaMode,
        line_string: this.mapMode == MapBoxMode.LineMode,
        trash: true
      },
      defaultMode: 'static'
    });

    this.applyStyleToDrawControl();

    this.map.addControl(this.drawControl);

    if (this.mapMode == MapBoxMode.GpsAreaMode && this.gpsArea) {
      this.addFeatureToMap();
      this.toggleAddPolygonDrawControl(false);
      this.centerMapToFeature();
    } else if (this.mapMode == MapBoxMode.LineMode && this.line) {
      this.addFeatureToMap();
      this.toggleAddLineDrawControl(false);
      this.centerMapToFeature();
    }
    if (this.isLocked) {
      this.toggleAddPolygonDrawControl(false);
      this.toggleAddLineDrawControl(false);
      this.toggleDeleteDrawControl(false);
    }

    this.map.on('draw.delete', this.handleDrawDeleteReference);
    this.map.on('draw.update', this.handleDrawUpdateReference);
    this.map.on('draw.create', this.handleDrawCreateReference);
  }

  private handleDrawCreate(args: any): void {
    this.updateFeaturePosition(args);
  }

  private handleDrawUpdate(args: any): void {
    this.updateFeaturePosition(args);
  }

  private handleDrawDelete(): void {
    if (this.mapMode == MapBoxMode.GpsAreaMode) {
      this.toggleAddPolygonDrawControl(true);
    } else if (this.mapMode == MapBoxMode.LineMode) {
      this.toggleAddLineDrawControl(true);
    }
  }

  private updateFeaturePosition(args: any): void {
    if (args?.features?.length > 0) {
      if (this.mapMode == MapBoxMode.GpsAreaMode) {
        this._gpsArea = this.transformToDBCoordinatesString(args.features[0].geometry.coordinates[0]);;
        this.gpsAreaChange.emit(this._gpsArea);
        this.toggleAddPolygonDrawControl(false);
      } else if (this.mapMode == MapBoxMode.LineMode) {
        this._line = this.transformToDBCoordinatesString(args.features[0].geometry.coordinates);;
        this.lineChange.emit(this._line);
        this.toggleAddLineDrawControl(false);
      }
    }
  }

  private toggleAddPolygonDrawControl(enable: boolean): void {
    const drawControls = document.querySelectorAll('#' + this.mapId + ' .mapbox-gl-draw_polygon');
    drawControls.forEach(control => {
      (control as HTMLElement).style.display = enable ? 'block' : 'none';
    });

    if (enable) {
      this.drawControl?.changeMode('draw_polygon');
    }
  }

  private toggleAddLineDrawControl(enable: boolean): void {
    const drawControls = document.querySelectorAll('#' + this.mapId + ' .mapbox-gl-draw_line');
    drawControls.forEach(control => {
      (control as HTMLElement).style.display = enable ? 'block' : 'none';
    });
    if (enable) {
      this.drawControl?.changeMode('draw_line_string');
    }
  }

  private toggleDeleteDrawControl(enable: boolean): void {
    const drawControls = document.querySelectorAll('#' + this.mapId + ' .mapbox-gl-draw_trash');
    drawControls.forEach(control => {
      (control as HTMLElement).style.display = enable ? 'block' : 'none';
    });
  }

  private addFeatureToMap(): void {
    this.drawControl?.deleteAll();
    const feature: Feature<Geometry> = {
      type: 'Feature', // Explicitly set the type to "Feature"
      id: 'unique-id',
      properties: {},
      geometry: {
        type: this.mapMode == MapBoxMode.GpsAreaMode ? 'Polygon' : 'MultiLineString',
        coordinates: [
          this.transformToGeoJsonCoordinates(this.mapMode == MapBoxMode.GpsAreaMode ? this._gpsArea : this._line)
        ]
      }
    };
    this.drawControl?.add(feature);
  }

  private setHeight(): void {
    const matchParentElements = this.el.nativeElement.querySelectorAll('#' + this.mapId);
    matchParentElements.forEach(element => {
      this.renderer.setStyle(element, 'height', this.height);
    });
  }

  private transformToGeoJsonCoordinates(coordinatesString: string): number[][] {
    // Split the string by semicolons to get individual coordinate pairs
    const coordinatePairs = coordinatesString.split(';').filter(pair => pair.trim() !== '');

    // Transform the coordinate pairs into the desired array format
    const transformedCoordinates = coordinatePairs.map(pair => {
      const [lat, lng] = pair.split(',').map(Number);
      return [lng, lat];
    });

    //Old google maps coordinates are handeled differently
    //MapBox map needs the first and last coordinates to be the same to close the polygon
    //If the first and last coordinates are not the same, add the first coordinate to the end of the array
    if (transformedCoordinates.length > 0) {
      const firstCoordinate = transformedCoordinates[0];
      const lastCoordinate = transformedCoordinates[transformedCoordinates.length - 1];
      if (firstCoordinate[0] !== lastCoordinate[0] || firstCoordinate[1] !== lastCoordinate[1]) {
        transformedCoordinates.push(firstCoordinate);
      }
    }

    return transformedCoordinates;
  }

  private transformToDBCoordinatesString(coordinates: number[][]): string {
    const transformedCoordinates = coordinates.map(pair => pair.reverse().join(',')).join(';');
    return transformedCoordinates;
  }

  private refocusMap(): void {
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.centerMapToFeature();
    }
    else if (this.mapMode == MapBoxMode.PositionMode) {
      this.centerMapToPosition();
    }
  }

  private centerMapToFeature(): void {
    if (this.drawControl && this.drawControl.getAll().features.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      this.drawControl.getAll().features.forEach(feature => {
        feature.geometry.coordinates[0].forEach((coord: [number, number]) => {
          bounds.extend(coord);
        });
      });

      this.map.fitBounds(bounds, {
        padding: 20
      });
    }
  }

  private toggleSearchControl(): void {
    if (this.isLocked) {
      if (this.map.hasControl(this.geoCoderControl)) {
        this.map.removeControl(this.geoCoderControl);
      }
    } else {
      this.map.addControl(this.geoCoderControl, 'top-left');
    }
  }

  private addPositionMarkerAndHandleChanges(): void {
    if (this.position) {
      const [lat, lng] = this.position.split(',').map(Number);
      this.marker = new mapboxgl.Marker({color: '#006633' })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.centerMapToPosition();
    }
    if (!this.isLocked) {
      this.map.on('click', this.newPositionSelectedReference);
    }
  }

  private centerMapToPosition(): void {
    if (this.position) {
      const [lat, lng] = this.position.split(',').map(Number);
      this.map.flyTo({
        center: [lng, lat],
        zoom: 15
      });
    }
  }

  private togglePositionListener(): void {
    if (this.isLocked) {
      this.map.off('click', this.newPositionSelectedReference);
    } else {
      this.map.on('click', this.newPositionSelectedReference);
    }
  }

  private geoCoderNewPositionSelected(e: any): void {
    this.position = `${e.result.center[1]},${e.result.center[0]}`;
    this.marker ? this.marker.setLngLat([e.result.center[0], e.result.center[1]]) :
      this.marker = new mapboxgl.Marker({color: '#006633' })
        .setLngLat([e.result.center[0], e.result.center[1]])
        .addTo(this.map);

    this.geoCoderControl.mapMarker.remove();
    this.positionChange.emit(this.position);
  }

  private newPositionSelected(e: mapboxgl.MapMouseEvent & mapboxgl.EventData): void {
    this.position = `${e.lngLat.lat},${e.lngLat.lng}`;
    this.marker ? this.marker.setLngLat([e.lngLat.lng, e.lngLat.lat]) :
      this.marker = new mapboxgl.Marker({color: '#006633' })
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .addTo(this.map);

    this.geoCoderControl.clear();
    this.positionChange.emit(this.position);
  }

  private handleReadyOnlyMode(): void {
    //Remove all controls if map is read only
    if (this.isReadOnly) {
      this.isLocked = true;
      this.toggleSearchControl();
      this.togglePositionListener();
      this.toggleAddPolygonDrawControl(false);
      this.toggleAddLineDrawControl(false);
      this.toggleDeleteDrawControl(false);
    }
  }

  private applyStyleToDrawControl(): void {
    if (this.mapMode == MapBoxMode.GpsAreaMode) {
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-fill-inactive.cold').paint['fill-color'] = '#006633';
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-fill-inactive.cold').paint['fill-opacity'] = 0.6;
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-stroke-inactive.cold').paint['line-color'] = '#006633';
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-fill-static.hot').paint['fill-color'] = '#006633';
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-fill-static.hot').paint['fill-opacity'] = 0.6;
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-polygon-stroke-static.hot').paint['line-color'] = '#006633';
    } else if (this.mapMode == MapBoxMode.LineMode) {
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-line-inactive.cold').paint['line-color'] = '#FFDE21';
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-line-inactive.cold').paint['line-width'] = 5;
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-line-static.hot').paint['line-color'] = '#FFDE21';
      this.drawControl.options.styles.find(style => style.id === 'gl-draw-line-static.hot').paint['line-width'] = 5;
    }
  }
}
